import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Единый загрузочный компонент.
 *
 * variants:
 *  - 'inline'      — компактный, рядом с другим текстом (default)
 *  - 'block'       — по центру блока (min-h-[40vh])
 *  - 'fullscreen'  — на весь экран
 *  - 'card'        — крупный, в карточке с заголовком/подписью
 *  - 'button'      — мелкий, для кнопок (без обёртки)
 *  - 'overlay'     — прозрачный оверлей поверх контейнера
 */
const SIZES = {
  xs: 14,
  sm: 18,
  md: 24,
  lg: 32,
  xl: 44,
};

export default function Loading({
  variant = 'inline',
  size = 'md',
  label,
  description,
  className = '',
}) {
  const px = SIZES[size] || SIZES.md;

  if (variant === 'button') {
    return <Loader2 className={`animate-spin ${className}`} style={{ width: px, height: px }} aria-hidden="true" />;
  }

  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 ${className}`}>
        <Loader2 className="animate-spin" style={{ width: px, height: px }} aria-hidden="true" />
        {label && <span className="text-sm">{label}</span>}
      </span>
    );
  }

  const Icon = (
    <Loader2
      className="animate-spin text-indigo-600 dark:text-indigo-400"
      style={{ width: px, height: px }}
      aria-hidden="true"
    />
  );

  if (variant === 'block') {
    return (
      <div className={`flex flex-col items-center justify-center py-12 min-h-[40vh] ${className}`} role="status" aria-live="polite">
        {Icon}
        {label && <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{label}</p>}
        {description && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{description}</p>}
      </div>
    );
  }

  if (variant === 'fullscreen') {
    return (
      <div className={`fixed inset-0 flex flex-col items-center justify-center bg-white/70 dark:bg-slate-950/70 backdrop-blur-sm z-50 ${className}`} role="status" aria-live="polite">
        {Icon}
        {label && <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>}
        {description && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>}
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      <div className={`absolute inset-0 flex flex-col items-center justify-center bg-white/60 dark:bg-slate-950/60 backdrop-blur-sm rounded-inherit z-10 ${className}`} role="status" aria-live="polite">
        {Icon}
        {label && <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{label}</p>}
      </div>
    );
  }

  // card
  return (
    <div className={`flex items-center justify-center py-12 ${className}`} role="status" aria-live="polite">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur shadow-sm p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-500/10">
          {Icon}
        </div>
        {label && <h3 className="text-base font-semibold text-slate-900 dark:text-white">{label}</h3>}
        {description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>}
      </div>
    </div>
  );
}
